import React, { useCallback, useEffect, useMemo, useState } from 'react';

const SLIDE_INTERVAL_MS = 6000;
const MANIFEST_URL = '/slides/manifest.json';

function slideSrc(name: string): string {
  return `/slides/${encodeURIComponent(name)}`;
}

export const HomeSlideshow: React.FC = () => {
  const [filenames, setFilenames] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(MANIFEST_URL)
      .then((r) => (r.ok ? r.json() : []))
      .then((data: unknown) => {
        if (cancelled) return;
        if (!Array.isArray(data)) {
          setFilenames([]);
          return;
        }
        const names = data.filter((x): x is string => typeof x === 'string' && x.length > 0);
        setFilenames(names);
      })
      .catch(() => {
        if (!cancelled) setFilenames([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const count = filenames.length;

  const goNext = useCallback(() => {
    setIndex((i) => (count ? (i + 1) % count : 0));
  }, [count]);

  const goPrev = useCallback(() => {
    setIndex((i) => (count ? (i - 1 + count) % count : 0));
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused || reducedMotion) return;
    const id = window.setInterval(goNext, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [count, paused, reducedMotion, goNext]);

  const currentName = useMemo(() => filenames[index] ?? '', [filenames, index]);

  if (count === 0) {
    return null;
  }

  return (
    <section
      className="home-slideshow"
      aria-roledescription="carousel"
      aria-label="Featured images"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="home-slideshow-viewport">
        {filenames.map((name, i) => (
          <div
            key={name}
            className={`home-slideshow-slide${i === index ? ' home-slideshow-slide--active' : ''}`}
            aria-hidden={i !== index}
          >
            <img
              src={slideSrc(name)}
              alt=""
              className="home-slideshow-img"
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
          </div>
        ))}
      </div>
      {count > 1 && (
        <>
          <button
            type="button"
            className="home-slideshow-nav home-slideshow-nav--prev"
            onClick={goPrev}
            aria-label="Previous slide"
          >
            ‹
          </button>
          <button
            type="button"
            className="home-slideshow-nav home-slideshow-nav--next"
            onClick={goNext}
            aria-label="Next slide"
          >
            ›
          </button>
          <div className="home-slideshow-dots" role="group" aria-label="Slide selection">
            {filenames.map((name, i) => (
              <button
                key={name}
                type="button"
                aria-label={`Go to slide ${i + 1} of ${count}`}
                aria-current={i === index ? 'true' : undefined}
                className={`home-slideshow-dot${i === index ? ' home-slideshow-dot--active' : ''}`}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
        </>
      )}
      <p className="home-slideshow-live" aria-live="polite">
        {currentName ? `Slide ${index + 1} of ${count}` : ''}
      </p>
    </section>
  );
};
